import { useEffect, useState } from "react";
import { useObserverStore } from "../../stores/observerStore";

const GOOD_COLORS: Record<string, string> = {
	fuel: "#e0af68",
	ore: "#bb9af7",
	food: "#9ece6a",
	tech: "#7aa2f7",
	luxuries: "#ff9e64",
	medicine: "#f7768e",
};

function formatRemaining(ms: number): string {
	if (ms <= 0) return "expired";
	const totalSec = Math.floor(ms / 1000);
	const h = Math.floor(totalSec / 3600);
	const m = Math.floor((totalSec % 3600) / 60);
	if (h > 0) return `${h}h ${m}m`;
	const s = totalSec % 60;
	return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatEventType(eventType: string): string {
	return eventType.replace(/_/g, " ").toUpperCase();
}

export function EventBanner(): React.JSX.Element | null {
	const galacticEvents = useObserverStore((s) => s.galacticEvents);
	const removeExpired = useObserverStore((s) => s.removeExpiredGalacticEvents);
	const [, setTick] = useState(0);

	// Countdown timer — re-render every second + prune expired
	useEffect(() => {
		if (galacticEvents.length === 0) return;
		const interval = setInterval(() => {
			setTick((t) => t + 1);
			removeExpired();
		}, 1000);
		return () => clearInterval(interval);
	}, [galacticEvents.length, removeExpired]);

	if (galacticEvents.length === 0) return null;

	return (
		<div
			style={{
				position: "absolute",
				top: "44px",
				left: 0,
				right: 0,
				display: "flex",
				gap: "0.5rem",
				padding: "0.4rem 1rem",
				background: "rgba(10, 10, 15, 0.8)",
				backdropFilter: "blur(8px)",
				borderBottom: "1px solid rgba(26, 26, 46, 0.8)",
				zIndex: 19,
				fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
				fontSize: "0.65rem",
				overflowX: "auto",
			}}
		>
			{galacticEvents.map((evt) => {
				const remaining = evt.expiresAt - Date.now();
				const isBullish = evt.multiplier > 1;
				const accentColor = isBullish ? "#f7768e" : "#73daca";
				const goodColor = GOOD_COLORS[evt.good] ?? "#c8c8d0";
				const arrow = isBullish ? "\u25B2" : "\u25BC";

				return (
					<div
						key={evt.id}
						style={{
							display: "flex",
							alignItems: "center",
							gap: "0.5rem",
							padding: "0.25rem 0.6rem",
							background: `${accentColor}10`,
							border: `1px solid ${accentColor}30`,
							borderRadius: "4px",
							whiteSpace: "nowrap",
							flexShrink: 0,
						}}
					>
						<span style={{ color: accentColor, fontWeight: 700 }}>{arrow}</span>
						<span style={{ color: "#c8c8d0", fontWeight: 600 }}>
							{formatEventType(evt.eventType)}
						</span>
						<span
							style={{
								color: goodColor,
								padding: "0.1rem 0.3rem",
								background: `${goodColor}15`,
								borderRadius: "2px",
							}}
						>
							{evt.good}
						</span>
						<span style={{ color: "#565680" }}>
							z{evt.zoneMin}-{evt.zoneMax}
						</span>
						<span style={{ color: accentColor, fontWeight: 600 }}>
							{evt.multiplier.toFixed(2)}x
						</span>
						<span style={{ color: remaining < 600_000 ? "#f7768e" : "#565680" }}>
							{formatRemaining(remaining)}
						</span>
					</div>
				);
			})}
		</div>
	);
}
